import { useState, useEffect } from 'react'
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom'
import useAuthStore from '../../stores/authStore'
import { authApi } from '../../services/api'
import { useFiltroMenu, BuscadorMenu } from './BuscadorMenu'
import NotificationBell from '../notifications/NotificationBell'
import AlertaStockFlotante from '../comedores/AlertaStockFlotante'
import ModoPantallaBoton from './ModoPantallaBoton'
import { hasSection, isAdmin as isAdminUser } from '../../config/permissions'
import WhatsNewModal from '../common/WhatsNewModal'
import { APP_VERSION } from '../../config/version'
import ErrorBoundary from '../common/ErrorBoundary'

const Layout = () => {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, logout } = useAuthStore()
  const isHospital = user?.modulo === 'hospital'
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false)

  // Los permisos pueden cambiar con la sesión abierta (un admin saca o da un acceso):
  // se releen al entrar, cada 5 minutos y al volver a la pestaña.
  useEffect(() => {
    const releer = () => {
      authApi.me()
        .then((r) => {
          const d = r.data?.data
          if (!d) return
          const actual = useAuthStore.getState().user
          if (actual) useAuthStore.getState().setUser({ ...actual, rol: d.rol, modulo: d.modulo, permisos: d.permisos || [] })
        })
        .catch(() => {}) // sin red: se sigue con los permisos que ya tiene
    }
    releer()
    const intervalo = setInterval(releer, 5 * 60 * 1000)
    const alVolver = () => { if (document.visibilityState === 'visible') releer() }
    document.addEventListener('visibilitychange', alVolver)
    return () => { clearInterval(intervalo); document.removeEventListener('visibilitychange', alVolver) }
  }, [])

  const handleLogout = async () => {
    await logout()
    navigate('/login')
  }

  const normalizeRole = (role) => {
    const r = String(role || '').trim().toLowerCase()
    if (!r) return ''
    if (r === 'operador' || r === 'data_entry' || r === 'data-entry') return 'data_entry'
    if (r === 'supervisor' || r === 'usuario' || r === 'user') return 'usuario'
    if (r === 'admin' || r === 'administrator') return 'admin'
    if (r === 'lector' || r === 'read_only' || r === 'readonly') return 'lector'
    return r
  }

  const isAdmin = isAdminUser(user)
  const can = (section) => hasSection(user, section)

  const [collapsedGroups, setCollapsedGroups] = useState({
    general: false,
    programas: false,
    logistica: false,
    config: false
  })

  const toggleGroup = (key) => {
    setCollapsedGroups(prev => ({
      ...prev,
      [key]: !prev[key]
    }))
  }

  const menuGroups = [
    {
      key: 'general',
      label: 'General',
      items: [
        { name: 'Panel', href: '/panel', icon: HomeIcon, tooltip: 'Panel general' },
        ...(can('buscar') ? [{ name: 'Buscar', href: '/buscar', icon: SearchIcon, tooltip: 'Buscar por DNI o nombre' }] : []),
        ...(can('beneficiarios') ? [{ name: 'Beneficiarios', href: '/beneficiarios', icon: BeneficiariosIcon, tooltip: 'Padrón de beneficiarios' }] : []),
        ...(can('analytics') ? [{ name: 'Analytics', href: '/analytics', icon: ChartIcon, tooltip: 'Análisis y mapa' }] : []),
      ]
    },
    {
      key: 'programas',
      label: 'Programas Sociales',
      items: [
        ...(can('caja_celiacos') ? [{ name: 'Caja Celíacos', href: '/caja-celiacos', icon: CeliacoIcon, tooltip: 'Entrega de cajas' }] : []),
        ...(can('comedores') ? [{ name: 'Comedores', href: '/comedores', icon: ComedorIcon, tooltip: 'Padrón de chicos y asistencia en la sede' }] : []),
        ...(can('comedores_admin') ? [{ name: 'Adm. Comedores', href: '/comedores-administracion', icon: ComedorIcon, tooltip: 'Mercadería, gastos y costo por ración' }] : []),
        ...(can('tarjeta_sapucay') ? [{ name: 'Tarjeta Sapucay', href: '/tarjeta-sapucay', icon: SapucayIcon, tooltip: 'Entregas Sapucay' }] : []),
        ...(can('tarjeta_mama_mbarete') ? [{ name: 'Mamá Mbareté', href: '/tarjeta-mama-mbarete', icon: MamaMbareteIcon, tooltip: 'Padrón Mamá Mbareté' }] : []),
        ...(can('kit_femenino') ? [{ name: 'Kit Femenino', href: '/kit-femenino', icon: MamaMbareteIcon, tooltip: 'Planilla mensual de kits femeninos' }] : []),
        ...(can('inundaciones') ? [{ name: 'Inundaciones', href: '/inundaciones', icon: ShieldIcon, tooltip: 'Evacuados y contingencia' }] : []),
        ...(can('el_nino') ? [{ name: 'Fenómeno El Niño', href: '/el-nino', icon: ShieldIcon, tooltip: 'Relevamiento social por inundación' }] : []),
        ...(can('reclamos') ? [{ name: 'Reclamos', href: '/reclamos', icon: MesaIcon, tooltip: 'Alumbrado, residuos, poda y calles' }] : []),
        ...(can('capacitaciones') ? [{ name: 'Capacitaciones', href: '/capacitaciones', icon: AuditIcon, tooltip: 'Capacitaciones y asistencia' }] : []),
        ...(can('produccion') ? [{ name: 'Producción', href: '/produccion', icon: CeliacoIcon, tooltip: 'Entregas de semillas, plantines y abonos' }] : []),
        ...(can('instituciones') ? [{ name: 'Instituciones', href: '/instituciones', icon: ComedorIcon, tooltip: 'Escuelas, iglesias y clubes: pedidos de recursos' }] : []),
        ...(can('emprendedores') ? [{ name: 'Emprendedores', href: '/emprendedores', icon: ValeIcon, tooltip: 'Emprendedores y ayudas' }] : []),
      ]
    },
    {
      key: 'logistica',
      label: 'Logística y Transporte',
      items: [
        ...(can('viajes_corrientes') ? [{ name: 'Combi Corrientes', href: '/viajes-corrientes', icon: MapIcon, tooltip: 'Combi a Corrientes' }] : []),
        ...(can('viajes_goya') ? [{ name: 'Combi Goya', href: '/viajes-goya', icon: MapIcon, tooltip: 'Combi a Goya' }] : []),
        ...(can('viajes_camioneta_mn') ? [{ name: 'Camioneta AF802MN', href: '/viajes-camioneta-mn', icon: MapIcon, tooltip: 'Camioneta AF802MN' }] : []),
        ...(can('viajes_camioneta_mo') ? [{ name: 'Camioneta AF802MO', href: '/viajes-camioneta-mo', icon: MapIcon, tooltip: 'Camioneta AF802MO' }] : []),
        ...(can('viajes_colectivo') ? [{ name: 'Colectivo', href: '/viajes-colectivo', icon: MapIcon, tooltip: 'Colectivo · 33 butacas' }] : []),
        ...(can('viajes_camion') ? [{ name: 'Camión', href: '/viajes-camion', icon: MapIcon, tooltip: 'Camión · 30 espacios' }] : []),
        ...(can('viajes_captiva') ? [{ name: 'Chevrolet Captiva', href: '/viajes-captiva', icon: MapIcon, tooltip: 'Chevrolet Captiva · 6 lugares, destino según el día' }] : []),
        ...(can('viajes_padron') ? [{ name: 'Padrón de Viajes', href: '/viajes-padron', icon: AuditIcon, tooltip: 'Padrón de pasajeros' }] : []),
      ]
    },
    {
      key: 'config',
      label: 'Administración',
      items: [
        ...(can('mesa_entrada') ? [{ name: 'Mesa de Entrada', href: '/mesa-entrada', icon: MesaIcon, tooltip: 'Trámites y reclamos' }] : []),
        ...(can('whatsapp') ? [{ name: 'WhatsApp', href: '/whatsapp', icon: WhatsAppIcon, tooltip: 'WhatsApp · experimental' }] : []),
        ...(can('vales') ? [{ name: 'Vales para Retirar', href: '/vales', icon: ValeIcon, tooltip: 'Vales de mercadería' }] : []),
        ...(can('revision_duplicados') ? [{ name: 'Revisión', href: '/revision-duplicados', icon: AuditIcon, tooltip: 'Registros duplicados' }] : []),
        ...(can('tipos_asistencia') ? [{ name: 'Tipos', href: '/admin/tipos-asistencia', icon: SettingsIcon, tooltip: 'Tipos de asistencia' }] : []),
        ...(isAdmin ? [{ name: 'Usuarios', href: '/usuarios', icon: UsersIcon, tooltip: 'Usuarios y permisos' }] : []),
        ...(can('logs') ? [{ name: 'Logs', href: '/logs', icon: LogsIcon, tooltip: 'Auditoría del sistema' }] : []),
        { name: 'Soporte', href: '/soporte', icon: SoporteIcon, tooltip: 'Soporte técnico' },
      ]
    }
  ].filter(g => g.items.length > 0)

  // Bottom nav mobile (4 tabs fijos)
  const bottomNavItems = [
    { name: 'Inicio', href: '/panel', icon: HomeIcon },
    ...(can('buscar') ? [{ name: 'Buscar', href: '/buscar', icon: SearchIcon }] : []),
    { name: 'Asistencia', href: '/asistencia/registrar', icon: PlusIcon },
    { name: 'Perfil', href: null, action: () => setMobileMenuOpen(true), icon: UserNavIcon },
  ]

  const isActive = (path) => location.pathname === path

  const cn = (...classes) => classes.filter(Boolean).join(' ')

  // Color primario según módulo
  const moduleColor = isHospital ? 'bg-hospital-700' : 'bg-primary'
  const moduleActiveColor = isHospital ? 'text-hospital-600' : 'text-primary'

  // ── Tokens de tema del sidebar según módulo ──
  // Acción Social: verde más claro/vivo (acento #16DE27).
  const sidebarGradient = isHospital
    ? 'bg-hospital-800'
    : 'bg-[#0d6a22]'
  const accentBg = isHospital ? 'bg-hospital-400' : 'bg-[#16DE27]'
  const accentRing = isHospital ? 'ring-hospital-400/30' : 'ring-[#16DE27]/40'
  const accentText = isHospital ? 'text-hospital-300' : 'text-[#8effa0]'
  const accentSoft = isHospital ? 'bg-hospital-500/15' : 'bg-[#16DE27]/20'
  const accentSofter = isHospital ? 'bg-hospital-500/20' : 'bg-[#16DE27]/30'

  // Buscador de módulos: con texto, los grupos se muestran filtrados y abiertos.
  const { filtro: filtroMenu, setFiltro: setFiltroMenu, grupos: gruposMenu, buscando } = useFiltroMenu(menuGroups)

  // ── Item de navegación reutilizable (sidebar + drawer) ──
  const renderNavItem = (item, onClick) => {
    const active = item.href && !item.external ? isActive(item.href) : false
    if (item.external) {
      return (
        <a
          key={item.name}
          href={item.href}
          target="_blank"
          rel="noreferrer"
          onClick={onClick}
          title={item.tooltip || item.name}
          className="group relative flex items-center gap-3 px-2.5 py-2.5 rounded-xl text-[13.5px] font-bold transition-all duration-200 text-white/90 hover:text-white hover:bg-white/15"
        >
          <span className="flex h-8 w-8 items-center justify-center rounded-lg transition-colors flex-shrink-0 text-amber-300 group-hover:text-amber-200">
            <item.icon className="h-[18px] w-[18px]" />
          </span>
          <span className="truncate">{item.name}</span>
          <span className="ml-auto text-[10px] font-black text-amber-300 bg-amber-400/25 px-1.5 py-0.5 rounded">SaaS</span>
        </a>
      )
    }
    return (
      <Link
        key={item.name}
        to={item.href}
        onClick={onClick}
        title={item.tooltip || item.name}
        className={cn(
          'group relative flex items-center gap-3 px-2.5 py-2.5 rounded-xl text-[13.5px] font-bold transition-all duration-200',
          active
            ? `${accentSoft} text-white font-extrabold shadow-sm`
            : 'text-white/90 hover:text-white hover:bg-white/15'
        )}
      >
        {active && (
          <span className={cn('absolute left-0 top-1/2 -translate-y-1/2 h-5 w-1 rounded-r-full', accentBg)} />
        )}
        <span
          className={cn(
            'flex h-8 w-8 items-center justify-center rounded-lg transition-colors flex-shrink-0',
            active ? `${accentSofter} ${accentText}` : 'text-white/90 group-hover:text-white'
          )}
        >
          <item.icon className="h-[18px] w-[18px]" />
        </span>
        <span className="truncate">{item.name}</span>
      </Link>
    )
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row bg-slate-50/50">
      <WhatsNewModal />
      
      {/* ======= DESKTOP SIDEBAR ======= */}
      <aside className={cn('hidden md:flex flex-col w-72 flex-shrink-0 text-slate-300 h-screen sticky top-0 z-30 overflow-y-auto scrollbar-hide shadow-2xl', sidebarGradient)}>
        {/* Sidebar header / Brand Logo */}
        <div className="px-5 pt-6 pb-5 flex items-center gap-3">
          <div className={cn('rounded-xl p-2 ring-1 flex-shrink-0 shadow-lg', accentSofter, accentRing)}>
            <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
          </div>
          <div className="min-w-0">
            <h2 className="text-white font-black text-lg tracking-tight leading-tight">San Roque</h2>
            <span className={cn('text-[10px] font-bold uppercase tracking-wider block', accentText)}>
              {isHospital ? 'Módulo Hospital' : 'Acción Social'}
            </span>
          </div>
        </div>

        {/* User Card */}
        <div className="mx-4 mb-2 rounded-2xl bg-white/[0.04] ring-1 ring-white/10 p-3 flex items-center justify-between gap-2">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className={cn('relative h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm uppercase shadow-inner', accentSofter, 'ring-1', accentRing)}>
              {user?.usuario?.substring(0, 2) || <UserIcon className="h-5 w-5" />}
              <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-[#16DE27] ring-2 ring-[#0d6a22]" />
            </div>
            <div className="leading-tight min-w-0">
              <div className="text-sm font-bold text-white truncate">{user?.usuario}</div>
              <div className="text-[10px] text-slate-400 capitalize truncate">{user?.rol}</div>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-white bg-red-600 hover:bg-red-700 shadow-md ring-2 ring-red-400/40 transition-all flex-shrink-0 font-extrabold text-xs uppercase tracking-wide cursor-pointer"
            title="Cerrar sesión"
          >
            <LogoutIcon className="h-4 w-4" />
            <span className="hidden xl:inline">Salir</span>
          </button>
        </div>

        <BuscadorMenu filtro={filtroMenu} setFiltro={setFiltroMenu} grupos={gruposMenu} />

        {/* Navigation menu */}
        <nav className="flex-1 px-3 py-2 space-y-3">
          {gruposMenu.map((group) => {
            const isCollapsed = !buscando && collapsedGroups[group.key]
            return (
              <div key={group.key} className="space-y-1">
                {/* Group Header */}
                <button
                  onClick={() => toggleGroup(group.key)}
                  className="w-full flex items-center justify-between text-left px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-300/80 hover:text-white transition-colors"
                >
                  <span>{group.label}</span>
                  <svg
                    className={`w-3.5 h-3.5 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>

                {/* Group Items */}
                {!isCollapsed && (
                  <div className="space-y-0.5">
                    {group.items.map((item) => renderNavItem(item))}
                  </div>
                )}
              </div>
            )
          })}
        </nav>

        {/* Sidebar footer */}
        <div className="px-5 py-4 mt-auto border-t border-white/10 text-[10.5px] font-medium text-white/50">
          <div className="text-white/70 font-semibold">Dir. de Modernización</div>
          <div className="text-white/60">Ricardo Fernández</div>
          <div className="mt-1 text-white/40">Intendente Cr. Raúl Pelozo</div>
        </div>
      </aside>

      {/* ======= MAIN LAYOUT WRAPPER ======= */}
      <div className="flex-1 flex flex-col min-h-screen min-w-0">

        {/* MOBILE HEADER */}
        <header className={`md:hidden sticky top-0 z-40 ${moduleColor} shadow-md flex h-14 items-center justify-between px-4 text-white`}>
          <Link to="/" className="flex items-center gap-2 min-w-0">
            <div className="rounded-lg bg-white/15 p-1 border border-white/20">
              <svg className="h-4 w-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <span className="font-bold text-sm">San Roque</span>
          </Link>

          <div className="flex items-center gap-2">
            <ModoPantallaBoton />
            <NotificationBell />
            <button
              type="button"
              onClick={() => setMobileMenuOpen(true)}
              className="p-1 rounded-lg text-white hover:bg-white/10"
            >
              <MenuIcon className="h-6 w-6" />
            </button>
          </div>
        </header>

        {/* DESKTOP TOP BAR */}
        <header className="hidden md:flex h-14 items-center justify-between px-8 border-b border-slate-200 bg-white shadow-sm sticky top-0 z-20">
          <div className="text-xs font-semibold text-slate-400 capitalize">
            {isHospital ? 'Área de Salud Municipal' : 'Dirección de Acción Social'}
          </div>
          <div className="flex items-center gap-4">
            <ModoPantallaBoton tone="dark" />
            <NotificationBell tone="dark" />
            <div className="h-5 w-px bg-slate-200" />
            <div className="flex items-center gap-2 text-slate-700">
              <div className="h-8 w-8 rounded-full bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-600 font-bold uppercase text-xs">
                {user?.usuario?.substring(0, 2)}
              </div>
              <span className="text-xs font-bold">{user?.usuario}</span>
            </div>
          </div>
        </header>

        {/* MOBILE MENU DRAWER */}
        {mobileMenuOpen && (
          <div
            className="md:hidden fixed inset-0 z-50 bg-black/60 backdrop-blur-xs flex justify-end"
            onClick={() => setMobileMenuOpen(false)}
          >
            <div
              className={cn('w-[19rem] max-w-[85vw] text-slate-300 h-full flex flex-col shadow-2xl overflow-y-auto scrollbar-hide', sidebarGradient)}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="px-5 pt-6 pb-4 flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <div className={cn('h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold text-sm uppercase ring-1', accentSofter, accentRing)}>
                    {user?.usuario?.substring(0, 2) || <UserIcon className="h-5 w-5" />}
                  </div>
                  <div className="min-w-0">
                    <div className="text-white font-bold text-sm leading-tight truncate">{user?.usuario}</div>
                    <div className="text-[10px] text-slate-400 capitalize mt-0.5 truncate">{user?.rol}</div>
                  </div>
                </div>
                <button
                  onClick={() => setMobileMenuOpen(false)}
                  className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-white/10 flex-shrink-0"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <BuscadorMenu filtro={filtroMenu} setFiltro={setFiltroMenu} grupos={gruposMenu} onElegir={() => setMobileMenuOpen(false)} />

              {/* Drawer Menu links */}
               <nav className="flex-1 px-3 py-2 space-y-3">
                 {gruposMenu.map((group) => {
                   const isCollapsed = !buscando && collapsedGroups[group.key]
                   return (
                     <div key={group.key} className="space-y-1">
                       <button
                         onClick={() => toggleGroup(group.key)}
                         className="w-full flex items-center justify-between text-left px-2.5 py-1 text-[10.5px] font-bold uppercase tracking-[0.12em] text-slate-300/80 hover:text-white"
                       >
                         <span>{group.label}</span>
                         <svg
                           className={`w-3.5 h-3.5 transition-transform duration-200 ${isCollapsed ? '-rotate-90' : ''}`}
                           fill="none"
                           stroke="currentColor"
                           viewBox="0 0 24 24"
                         >
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                         </svg>
                       </button>

                       {!isCollapsed && (
                         <div className="space-y-0.5">
                           {group.items.map((item) => renderNavItem(item, () => setMobileMenuOpen(false)))}
                         </div>
                       )}
                     </div>
                   )
                 })}
               </nav>

              <div className="px-4 py-4 mt-auto border-t border-white/10">
                <button
                  onClick={() => { setMobileMenuOpen(false); handleLogout(); }}
                  className="w-full flex items-center justify-center gap-2.5 px-4 py-3 rounded-xl text-xs font-black text-white bg-red-600 hover:bg-red-700 shadow-md ring-2 ring-red-400/40 transition-all uppercase tracking-wide"
                >
                  <LogoutIcon className="h-4.5 w-4.5" />
                  Cerrar Sesión
                </button>
              </div>
            </div>
          </div>
        )}

        {/* MAIN BODY CONTENT */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8">
          <ErrorBoundary clave={location.pathname} titulo="No se pudo mostrar esta pantalla">
            <Outlet />
            {can('comedores_admin') && <AlertaStockFlotante />}
          </ErrorBoundary>
        </main>

        {/* MOBILE BOTTOM NAVIGATION BAR */}
        <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t border-gray-200 shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
          <div className="grid grid-cols-4 h-16">
            {bottomNavItems.map((item) => {
              const active = item.href ? isActive(item.href) : false
              return (
                <button
                  key={item.name}
                  onClick={item.action || (() => navigate(item.href))}
                  className={`flex flex-col items-center justify-center gap-1 transition-all active:scale-95 ${active
                    ? `${moduleActiveColor} font-semibold`
                    : 'text-gray-400 hover:text-gray-600'
                    }`}
                >
                  {item.href === '/personas/nueva' ? (
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center shadow-md ${isHospital ? 'bg-hospital-700' : 'bg-primary'} text-white -mt-4`}>
                      <item.icon className="h-5 w-5" />
                    </div>
                  ) : (
                    <item.icon className="h-6 w-6" />
                  )}
                  <span className="text-[10px] font-medium">
                    {item.name}
                  </span>
                </button>
              )
            })}
          </div>
          <div className="h-safe-bottom bg-white" />
        </nav>

        {/* PIE DE PÁGINA */}
        <footer className="bg-white border-t border-slate-200 py-5 mb-20 md:mb-0 transition-colors">
          <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row justify-between items-center gap-4 text-center md:text-left">
              {/* Autoridades y Gestión */}
              <div className="space-y-0.5">
                <div className="text-slate-600 text-xs sm:text-sm font-medium">
                  Dirección de Modernización: <span className="text-slate-900 font-bold">Ricardo Fernández</span>
                </div>
                <div className="text-slate-500 text-xs">
                  Intendente <span className="text-slate-900 font-bold">Cr. Raúl Pelozo</span> — Gestión 2025-2029
                </div>
              </div>

              {/* Versionado en pie de página */}
              <div className="flex items-center gap-2">
                <span className="inline-flex items-center gap-1.5 rounded-lg bg-slate-100 border border-slate-200 px-2.5 py-1 text-xs font-bold font-mono text-slate-700">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
                  v{APP_VERSION}
                </span>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </div>
  )
}

// ── Icons ──────────────────────────────────────────────────
function HomeIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
    </svg>
  )
}

function SearchIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
    </svg>
  )
}

function PlusIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
    </svg>
  )
}

function UserNavIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  )
}

function ChartIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
    </svg>
  )
}

function LogoutIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  )
}

function MenuIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
    </svg>
  )
}

function UserIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  )
}

function UsersIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  )
}

function ImportIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
    </svg>
  )
}

function LogsIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2a2 2 0 012-2h2a2 2 0 012 2v2m-8 0h8m-8 0H7a2 2 0 01-2-2V7a2 2 0 012-2h10a2 2 0 012 2v8a2 2 0 01-2 2h-2M9 9h6M9 12h4" />
    </svg>
  )
}

function AuditIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
    </svg>
  )
}

function BeneficiariosIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
    </svg>
  )
}

function SettingsIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
      />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  )
}

function OsintIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
    </svg>
  )
}

function SoporteIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 5.636l-3.536 3.536m0 5.656l3.536 3.536M9.172 9.172L5.636 5.636m3.536 9.192l-3.536 3.536M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-5 0a4 4 0 11-8 0 4 4 0 018 0z" />
    </svg>
  )
}

function MapIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
    </svg>
  )
}

function CeliacoIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  )
}

function ComedorIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12h18a9 9 0 01-18 0zm4-4c0-1 1-1.5 1-2.5S7 4 7 3m5 5c0-1 1-1.5 1-2.5S12 4 12 3m5 5c0-1 1-1.5 1-2.5S17 4 17 3" />
    </svg>
  )
}

function ValeIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
    </svg>
  )
}

function SapucayIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  )
}

function MamaMbareteIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
    </svg>
  )
}

function ShieldIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  )
}

function WhatsAppIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  )
}

function MesaIcon({ className }) {
  return (
    <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 4H6a2 2 0 00-2 2v12a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-2m-4-1v8m0 0l3-3m-3 3L9 8m-5 5h2.586a1 1 0 01.707.293l2.414 2.414a1 1 0 00.707.293h3.172a1 1 0 00.707-.293l2.414-2.414a1 1 0 01.707-.293H20" />
    </svg>
  )
}

export default Layout
